'use client'
/* eslint-disable */
// @ts-nocheck
import { ArrowLeftIcon, ArrowRightIcon, X, Repeat } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { cn } from '@/utils'
import { useToolbar } from './toolbar-provider'
import { type SearchAndReplaceStorage } from '../extensions/search-and-replace'

export function SearchAndReplaceToolbar() {
  const { editor, hasCommand, hasExtension } = useToolbar()
  const t = useTranslations('components.tiptap.toolbars.searchAndReplace')
  const isSearchAndReplaceEnabled =
    hasExtension('searchAndReplace') &&
    hasCommand('setSearchTerm') &&
    hasCommand('setReplaceTerm') &&
    hasCommand('setCaseSensitive') &&
    hasCommand('replace') &&
    hasCommand('replaceAll') &&
    hasCommand('selectNextResult') &&
    hasCommand('selectPreviousResult')

  const [open, setOpen] = useState(false)
  const [replacing, setReplacing] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [replaceText, setReplaceText] = useState('')
  const [checked, setChecked] = useState(false)

  const searchStorage = isSearchAndReplaceEnabled
    ? ((editor.storage as { searchAndReplace?: SearchAndReplaceStorage })
        .searchAndReplace ?? undefined)
    : undefined
  const results = searchStorage?.results ?? []
  const selectedResult = searchStorage?.selectedResult ?? 0

  const replace = () =>
    isSearchAndReplaceEnabled && editor?.chain().replace().run()
  const replaceAll = () =>
    isSearchAndReplaceEnabled && editor?.chain().replaceAll().run()
  const selectNext = () =>
    isSearchAndReplaceEnabled && editor?.chain().selectNextResult().run()
  const selectPrevious = () =>
    isSearchAndReplaceEnabled && editor?.chain().selectPreviousResult().run()

  useEffect(() => {
    if (!isSearchAndReplaceEnabled) {
      return
    }

    editor?.chain().setSearchTerm(searchText).run()
  }, [editor, isSearchAndReplaceEnabled, searchText])

  useEffect(() => {
    if (!isSearchAndReplaceEnabled) {
      return
    }

    editor?.chain().setReplaceTerm(replaceText).run()
  }, [editor, isSearchAndReplaceEnabled, replaceText])

  useEffect(() => {
    if (!isSearchAndReplaceEnabled) {
      return
    }

    editor?.chain().setCaseSensitive(checked).run()
  }, [checked, editor, isSearchAndReplaceEnabled])

  useEffect(() => {
    if (!open) {
      setReplaceText('')
      setSearchText('')
      setReplacing(false)
    }
  }, [open])

  if (!isSearchAndReplaceEnabled) {
    return null
  }

  return (
    <Popover open={open}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger disabled={!editor} asChild>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => {
                setOpen(!open)
              }}
              className={cn('h-8 w-max px-3 font-normal')}
            >
              <Repeat className="mr-2 h-4 w-4" />
              <p>{t('title')}</p>
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <span>{t('title')}</span>
        </TooltipContent>
      </Tooltip>

      <PopoverContent
        align="end"
        onCloseAutoFocus={(e) => {
          e.preventDefault()
        }}
        onEscapeKeyDown={() => {
          setOpen(false)
        }}
        className="relative flex w-[400px] px-3 py-2.5"
      >
        {!replacing ? (
          <div className={cn('relative flex items-center gap-1.5')}>
            <Input
              value={searchText}
              className="w-48"
              onChange={(e) => {
                setSearchText(e.target.value)
              }}
              placeholder={t('searchPlaceholder')}
            />
            <span>
              {results?.length === 0 ? selectedResult : selectedResult + 1}/
              {results?.length}
            </span>
            <Button
              onClick={selectPrevious}
              size="icon"
              variant="ghost"
              className="size-7"
            >
              <ArrowLeftIcon className="size-4 rtl:rotate-180" />
            </Button>
            <Button
              onClick={selectNext}
              size="icon"
              className="size-7"
              variant="ghost"
            >
              <ArrowRightIcon className="h-4 w-4 rtl:rotate-180" />
            </Button>
            <Separator orientation="vertical" className="mx-0.5 h-7" />
            <Button
              onClick={() => {
                setReplacing(true)
              }}
              size="icon"
              className="size-7"
              variant="ghost"
            >
              <Repeat className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => {
                setOpen(false)
              }}
              size="icon"
              className="size-7"
              variant="ghost"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className={cn('relative w-full')}>
            <X
              onClick={() => {
                setOpen(false)
              }}
              className="absolute top-3 right-3 h-4 w-4 cursor-pointer"
            />
            <div className="flex w-full items-center gap-3">
              <Button
                size="icon"
                className="size-7 rounded-full"
                variant="ghost"
                onClick={() => {
                  setReplacing(false)
                }}
              >
                <ArrowLeftIcon className="h-4 w-4 rtl:rotate-180" />
              </Button>
              <h2 className="text-sm font-medium">{t('searchAndReplace')}</h2>
            </div>

            <div className="my-2 w-full">
              <div className="mb-3">
                <Label className="text-gray-11 mb-1 text-xs">
                  {t('search')}
                </Label>
                <Input
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value)
                  }}
                  placeholder={t('searchPlaceholder')}
                />
                {results?.length === 0 ? selectedResult : selectedResult + 1}/
                {results?.length}
              </div>
              <div className="mb-2">
                <Label className="text-gray-11 mb-1 text-xs">
                  {t('replaceWith')}
                </Label>
                <Input
                  className="w-full"
                  value={replaceText}
                  onChange={(e) => {
                    setReplaceText(e.target.value)
                  }}
                  placeholder={t('replacePlaceholder')}
                />
              </div>
              <div className="mt-3 flex items-center space-x-2">
                <Checkbox
                  checked={checked}
                  onCheckedChange={(checked: boolean) => {
                    setChecked(checked)
                  }}
                  id="match_case"
                />
                <Label
                  htmlFor="match_case"
                  className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('matchCase')}
                </Label>
              </div>
            </div>

            <div className="actions mt-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  onClick={selectPrevious}
                  size="icon"
                  className="h-7 w-7"
                  variant="secondary"
                >
                  <ArrowLeftIcon className="h-4 w-4 rtl:rotate-180" />
                </Button>
                <Button
                  onClick={selectNext}
                  size="icon"
                  className="h-7 w-7"
                  variant="secondary"
                >
                  <ArrowRightIcon className="h-4 w-4 rtl:rotate-180" />
                </Button>
              </div>

              <div className="main-actions flex items-center gap-2">
                <Button
                  size="sm"
                  className="h-7 px-3 text-xs"
                  variant="secondary"
                  onClick={replaceAll}
                >
                  {t('replaceAll')}
                </Button>
                <Button
                  onClick={replace}
                  size="sm"
                  className="h-7 px-3 text-xs"
                >
                  {t('replace')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
